import { createTheme, Theme } from '@mui/material/styles';
import { colors } from './colors';
import { Breakpoint, fontSize, letterSpacing } from './fontFunctions';

// Required as per: https://mui.com/x/react-data-grid/getting-started/#typescript
// Augments theme type to include MuiDataGrid.
// noinspection ES6UnusedImports
import {} from '@mui/x-data-grid-pro/themeAugmentation';

export const AurionLightTheme: Theme = createTheme({
    // See: https://material-ui.com/customization/palette/

    palette: {
        primary: {
            main: colors.primary,
            dark: colors.primaryDark,
            light: colors.primaryLight,
            contrastText: colors.text.highOnDark,
        },
        secondary: {
            main: colors.secondary,
            dark: colors.secondaryDark,
            light: colors.secondaryLight,
            contrastText: colors.text.highOnDark,
        },
        grey: {
            300: colors.greyLight,
            500: colors.grey,
            700: colors.greyDark,
        },
        // Begin: Button colours (not warning/alert background colours)
        error: {
            main: colors.danger,
            light: colors.dangerLight,
        },
        warning: {
            main: colors.warning,
            light: colors.warningLight,
            contrastText: colors.text.highOnDark,
        },
        info: {
            main: colors.info,
            light: colors.infoLight,
            contrastText: colors.text.highOnDark,
        },
        success: { main: colors.success },
        // End: Button colours

        background: {
            default: colors.background.page,
            paper: colors.white,
        },
        divider: colors.divider,
    },
    typography: {
        // The default settings can be visualised at https://mui.com/customization/default-theme/?expand-path=$.typography
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
        h1: {
            fontFamily: 'Inter',
            // 96px = 6rem
            '@media (min-width:600px)': { fontSize: fontSize('h1', 6, Breakpoint.sm) },
            '@media (min-width:900px)': { fontSize: fontSize('h1', 6, Breakpoint.md) },
            '@media (min-width:1200px)': { fontSize: fontSize('h1', 6, Breakpoint.lg) },
            fontSize: fontSize('h1', 6, Breakpoint.xs),
            letterSpacing: letterSpacing(-1.5, 6), // -1.5px
        },
        h2: {
            fontFamily: 'Inter',
            // 60px = 3.75rem
            '@media (min-width:600px)': { fontSize: fontSize('h2', 3.75, Breakpoint.sm) },
            '@media (min-width:900px)': { fontSize: fontSize('h2', 3.75, Breakpoint.md) },
            '@media (min-width:1200px)': { fontSize: fontSize('h2', 3.75, Breakpoint.lg) },
            fontSize: fontSize('h2', 3.75, Breakpoint.xs),
            letterSpacing: letterSpacing(-0.5, 3.75), // -0.5px;
        },
        h3: {
            fontFamily: 'Inter',
            // 48px = 3rem
            '@media (min-width:600px)': { fontSize: fontSize('h3', 3, Breakpoint.sm) },
            '@media (min-width:900px)': { fontSize: fontSize('h3', 3, Breakpoint.md) },
            '@media (min-width:1200px)': { fontSize: fontSize('h3', 3, Breakpoint.lg) },
            fontSize: fontSize('h3', 3, Breakpoint.xs),
            letterSpacing: 'initial',
        },
        h4: {
            fontFamily: 'Inter',
            // 34px = 2.125rem
            '@media (min-width:600px)': { fontSize: fontSize('h4', 2.125, Breakpoint.sm) },
            '@media (min-width:900px)': { fontSize: fontSize('h4', 2.125, Breakpoint.md) },
            '@media (min-width:1200px)': { fontSize: fontSize('h4', 2.125, Breakpoint.lg) },
            fontSize: fontSize('h4', 2.125, Breakpoint.xs),
            letterSpacing: 'initial',
        },
        h5: {
            fontFamily: 'Inter',
            // 24px = 1.5rem
            '@media (min-width:600px)': { fontSize: fontSize('h5', 1.5, Breakpoint.sm) },
            '@media (min-width:900px)': { fontSize: fontSize('h5', 1.5, Breakpoint.md) },
            '@media (min-width:1200px)': { fontSize: fontSize('h5', 1.5, Breakpoint.lg) },
            fontSize: fontSize('h5', 1.5, Breakpoint.xs),
            letterSpacing: letterSpacing(0.18, 1.5), // 0.18px;
        },
        h6: {
            fontFamily: 'Inter',
            // 20px = 1.25rem
            '@media (min-width:600px)': { fontSize: fontSize('h6', 1.25, Breakpoint.sm) },
            '@media (min-width:900px)': { fontSize: fontSize('h6', 1.25, Breakpoint.md) },
            '@media (min-width:1200px)': { fontSize: fontSize('h6', 1.25, Breakpoint.lg) },
            fontSize: fontSize('h6', 1.25, Breakpoint.xs),
            letterSpacing: letterSpacing(0.15, 1.25), // 0.15px;
        },
        // Sizes should be specified in REM's (not pixels).  The base font size is 16px.
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 'bold',
            letterSpacing: letterSpacing(0.5, 1), // 0.5px (relative to 1rem)
            lineHeight: 1.5, // 24px (relative to 1rem)
        },
        subtitle2: {
            fontSize: `${14 / 16}rem`, // 14px
            fontWeight: 600,
            letterSpacing: letterSpacing(0.5, 14 / 16), // .5px (relative to 14px)
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 'normal',
            fontVariantNumeric: 'tabular-nums lining-nums', // Numerics monospacing
            letterSpacing: letterSpacing(0.5, 1), // 0.5px (relative to 1rem)
        },
        body2: {
            fontSize: `${14 / 16}rem`, // 14px
            fontWeight: 'normal',
            fontVariantNumeric: 'tabular-nums lining-nums', // Numerics monospacing
            lineHeight: 1.42857, // 20px (relative to 14px)
            letterSpacing: letterSpacing(0.5, 14 / 16), // 0.5px (relative to 14px)
        },
        button: {
            fontSize: `${14 / 16}rem`, // 14px
            fontWeight: 600,
            lineHeight: '1rem', // 16px
            letterSpacing: letterSpacing(1.25, 14 / 16), // 1.25px
            textTransform: 'none',
        },
        caption: {
            fontSize: `${12 / 16}rem`, // 12px
            fontWeight: 'normal',
            lineHeight: '1rem',
            letterSpacing: letterSpacing(0.5, 12 / 16), // 0.4px
        },
        overline: {
            fontWeight: 600,
            fontSize: `${10 / 16}rem`, // 10px
            lineHeight: '1rem',
            letterSpacing: letterSpacing(1.5, 10 / 16), // 1.5px
        },
    },
    breakpoints: {
        // Override the default MUI breakpoints (600/900/1200/1536)
        // Change md from default 900 -> 1024
        values: {
            xs: 0, // Mobile
            sm: 600, // Tablet
            md: 1024, // Laptop
            lg: 1200, // Desktop
            xl: 1536, // ..
        },
    },
});

AurionLightTheme.components = {
    MuiAppBar: {
        styleOverrides: {
            root: {
                backgroundColor: colors.background.mainNavigationBar,
                color: '#ffffff80', // white 50% (from Figma 01 Admin > Navigation)
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            root: ({ theme, ownerState: { severity, variant } }) => ({
                // Alert 'filled' variant does not use contrastText correctly.
                // https://github.com/mui/material-ui/issues/33512
                ...(variant === 'filled' &&
                    severity && {
                        color: theme.palette[severity].contrastText,
                    }),
            }),
        },
    },
    MuiCard: {
        defaultProps: {
            variant: 'outlined',
        },
        styleOverrides: {
            root: {
                borderRadius: '0.625rem',
            },
        },
    },
    MuiCardContent: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(2),

                ':last-child': {
                    paddingBottom: theme.spacing(2),
                },
            }),
        },
    },
    MuiAvatar: {
        styleOverrides: {
            root: ({ theme }) => ({
                backgroundColor: theme.palette.primary.main,
                width: '2.25rem',
                height: '2.25rem',
            }),
        },
    },
    MuiButton: {
        defaultProps: {
            variant: 'outlined',
            size: 'medium',
            color: 'primary',
        },
        styleOverrides: {
            root: ({ theme, ownerState: { color, variant = 'outlined', size = 'medium' } }) => {
                type CSSProperties = {
                    [key: string]: string | number | CSSProperties;
                };
                const buttonHeight = {
                    small: 24,
                    medium: 32,
                    large: 40,
                };

                // css config for each variant type
                const variantMapping: Record<string, CSSProperties> = {
                    outlined: {
                        border: '1px solid',
                        borderColor:
                            color && color !== 'inherit' ? theme.palette[color].main : 'default',
                        color: color && color !== 'inherit' ? theme.palette[color].main : 'default',
                    },
                    text: {
                        border: 'none',
                        color: color && color !== 'inherit' ? theme.palette[color].main : 'default',
                    },
                    contained: {
                        color: 'white',
                        backgroundColor:
                            color && color !== 'inherit' ? theme.palette[color].main : 'default',
                    },
                };

                const cssConfig = {
                    // Default css for all buttons
                    lineHeight: '16px',
                    minHeight: `${buttonHeight[size]}px`,
                    borderRadius: '6px',
                    // variant specific styling
                    ...variantMapping[variant],
                    '&:disabled': {
                        opacity: 0.38,
                        borderColor: 'inherit',
                        color:
                            color && color !== 'inherit'
                                ? theme.palette[color].contrastText
                                : 'default',
                        backgroundColor:
                            color && color !== 'inherit' ? theme.palette[color].main : 'default',
                    },
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                };

                return {
                    ...cssConfig,
                };
            },
        },
    },
    MuiInput: {
        styleOverrides: {
            underline: {
                '&:after': {
                    borderBottomColor: AurionLightTheme.palette.primary.dark,
                },
            },
        },
    },
    MuiFilledInput: {
        styleOverrides: {
            underline: {
                '&:after': {
                    borderBottomColor: AurionLightTheme.palette.primary.dark,
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                '&:focused :notchedOutline': {
                    borderColor: AurionLightTheme.palette.primary.dark,
                },
            },
            notchedOutline: {
                paddingLeft: '9px', // legend label spacing (default: 8px)
                paddingRight: '9px', // legend label spacing (default: 8px)
                '& legend > span': {
                    paddingLeft: '4px', // legend label spacing (default: 5px)
                    paddingRight: '4px', // legend label spacing (default: 5px)
                },
            },
        },
    },
    MuiFormLabel: {
        styleOverrides: {
            root: {
                '&:focused': {
                    color: AurionLightTheme.palette.primary.dark,
                },
            },
        },
    },
    MuiAccordion: {
        styleOverrides: {
            root: {
                borderRadius: '10px !important',
            },
        },
    },
    MuiDataGrid: {
        styleOverrides: {
            root: {
                height: '100%',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '10px',

                '.MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.greyLight,
                    color: colors.text.highOnDark,
                    borderRadius: '0',
                    fontSize: '1rem',
                    fontWeight: 'normal',
                },

                '&.MuiDataGrid-root .MuiDataGrid-columnHeaders': {
                    backgroundColor: AurionLightTheme.palette.grey['300'],
                    color: AurionLightTheme.palette.common.white,
                },

                '&.MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                    backgroundColor: AurionLightTheme.palette.grey['300'],
                    color: AurionLightTheme.palette.common.white,
                },

                '&.MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderTitle': {
                    color: AurionLightTheme.palette.common.white,
                    fontWeight: 600,
                },

                '&.MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiButtonBase-root': {
                    color: AurionLightTheme.palette.common.white,
                },

                '&.MuiDataGrid-root .MuiDataGrid-columnHeaders .Mui-disabled': {
                    color: AurionLightTheme.palette.grey['500'],
                },

                '.MuiDataGrid-row': {
                    color: AurionLightTheme.palette.grey['700'],
                    backgroundColor: AurionLightTheme.palette.background.paper,
                    fontVariantNumeric: 'tabular-nums lining-nums',
                    ...AurionLightTheme.typography.body1,

                    '.MuiButtonBase-root': {
                        color: AurionLightTheme.palette.primary.main,
                    },
                    '.MuiButton-containedPrimary': {
                        color: AurionLightTheme.palette.common.white,
                    },
                    '&.Mui-disabled, .Mui-disabled': {
                        color: AurionLightTheme.palette.grey.A400,
                    },
                    '&.Striped': {
                        backgroundColor: AurionLightTheme.palette.grey['200'],
                    },
                },

                '.MuiDataGrid-pinnedColumnHeaders': {
                    backgroundColor: AurionLightTheme.palette.grey['300'],
                },

                // Adds '...' to overflow of tree data grouping cells when the content is too long
                '.MuiDataGrid-cell': {
                    '.MuiDataGrid-treeDataGroupingCell': {
                        overflow: 'hidden',

                        span: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    },
                    padding: '1.125rem 0.625rem',
                },

                '& .MuiDataGrid-checkboxInput': {
                    padding: 0,
                },
                '& .MuiIconButton-root': {
                    padding: 0,
                },

                // INFO: This is for hiding master detail toggle button when we don't want to display it
                '.MuiDataGrid-detailPanelToggleCell.Mui-disabled': { display: 'none' },

                '.MuiDataGrid-virtualScroller': {
                    backgroundColor: AurionLightTheme.palette.background.paper,
                },

                '.MuiDataGrid-footerContainer': {
                    borderColor: AurionLightTheme.palette.divider,
                    backgroundColor: AurionLightTheme.palette.background.paper,
                    borderRadius: '0 0 10px 10px',
                },

                '.hideRightSeparator': {
                    '& > .MuiDataGrid-columnSeparator': {
                        visibility: 'hidden',
                    },
                },

                '.MuiDataGrid-toolbar': {
                    borderBottom: 0,
                    color: AurionLightTheme.palette.common.white,
                    backgroundColor: AurionLightTheme.palette.primary.main,
                    padding: '10px 16px 0 16px',
                    borderRadius: '10px 10px 0 0',

                    '.MuiSwitch-track': {
                        opacity: '0.33',
                    },

                    '.MuiButtonBase-root': {
                        color: 'inherit',
                        '&.MuiButton-root: hover': {
                            backgroundColor: AurionLightTheme.palette.primary.light,
                        },
                        '&:disabled': {
                            opacity: '0.38',
                        },

                        '&.MuiSwitch-switchBase': {
                            color: colors.greyLight,

                            '&.Mui-checked': {
                                color: colors.white,
                                '+.MuiSwitch-track': {
                                    backgroundColor: colors.white,
                                    opacity: '0.33',
                                },
                            },
                        },
                    },
                    '.MuiTabs-indicator': {
                        backgroundColor: AurionLightTheme.palette.common.white,
                    },
                    '.MuiInputBase-root': {
                        backgroundColor: AurionLightTheme.palette.common.white,
                        borderRadius: '8px',
                        height: '34px',
                        '.MuiSvgIcon-root': {
                            color: AurionLightTheme.palette.primary.main,
                        },
                        fieldset: {
                            top: '0',
                            '& > legend': {
                                display: 'none',
                            },
                        },
                    },
                },
                '&.MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
                    display: 'none',
                },
                '&.MuiDataGrid-root .MuiDataGrid-scrollbarFiller': {
                    display: 'none',
                },
            },
            cell: {
                fontSize: `${14 / 16}rem`, // 14px
            },
        },
    },
    MuiChip: {
        defaultProps: {
            color: 'default',
        },
        styleOverrides: {
            root: ({ ownerState }) => {
                const chipColour = ownerState.color as AurChipColours;

                return {
                    fontWeight: 600,
                    paddingRight: '4px',
                    paddingLeft: '4px',
                    '.MuiChip-iconColorDefault': {
                        color: 'white',
                    },
                    ...colors.chips[chipColour],
                };
            },
        },
    },
    MuiTextField: {
        styleOverrides: {
            root: ({ theme }) => ({
                // Make the icon button for DatePicker's our primary color.
                'svg[data-testid="CalendarIcon"]': {
                    color: theme.palette.primary.main,
                },
            }),
        },
    },
};

export type AurChipColours = keyof typeof colors.chips;

export default AurionLightTheme;

function as<T>(x: unknown): T {
    return x as T;
}
